import axios, { AxiosInstance } from "axios";
import { KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN, KEY_AZURE_TOKEN, REACT_CONFIG, KEY_LOGIN_ACCESS_TOKEN } from "../Helpers/constant";
import { AuthUser, LoginData, VerifySignUpFormData } from "../interfaces/Auth";
import { EventFormValues, RegisterEvent } from "../interfaces/Events";
import { PaymentData } from "../interfaces/Common";
import { ApplyJob, FormPostJob, FormPostJobFavorite, JobApplyDetails, JobBookmarkDetails, JobDetails, JobUploadDetails } from "../interfaces/Jobs";
import { PreferenceDetails, EventPreferenceDetails, MentorshipPreferenceDetails, JobPreferenceDetails, EmployeementDetails, CertificateDetails, AcademicRecords } from "../interfaces/Profile";
import { TaskAddDetails } from "../interfaces/MentorShip";
import { CreateSurvey } from "../interfaces/Survey";
import { Questionnaire } from "../interfaces/Service";

let appConfig: { apiBaseUrl: string; tokenUrl: string } | undefined;
export let axiosInstance: AxiosInstance;

const turbosendURL = "https://turbosend-dev-bam-api.turboverse.co/";

export const getAxiosInstance = async () => {
  appConfig = REACT_CONFIG;
  if (axiosInstance) {
    return axiosInstance;
  }
  axiosInstance = axios.create({
    baseURL: `${appConfig?.apiBaseUrl}`,
    timeout: 15000,
  });

  axiosInstance.interceptors.request.use((req) => {
    const token = localStorage.getItem(KEY_ACCESS_TOKEN);
    req.headers["access-token"] = localStorage.getItem(KEY_LOGIN_ACCESS_TOKEN);
    req.headers.Authorization = `Bearer ${token}`;
    return req;
  });

  //Response interceptor for API calls
  axiosInstance.interceptors.response.use(
    (resp) => {
      if (resp?.data?.success == false) {
        console.error("Response Interceptor Error:", resp.data.errorMessage);
      }
      return resp;
    },
    async function (error) {
      // Handle errors without causing property redefinition issues
      if (error.response?.status !== 401) {
        return Promise.reject(error);
      }
      // Handle 401 Unauthorized error
      if (error.response.status === 401) {
        try {
          const refreshToken = localStorage.getItem(KEY_REFRESH_TOKEN);
          const azureToken = localStorage.getItem(KEY_AZURE_TOKEN);

          if (!refreshToken) {
            localStorage.clear();
            window.location.reload();
          }

          const REFRESH_URL = `${appConfig?.apiBaseUrl}/refreshToken`;

          const res = await axios.post(
            REFRESH_URL,
            { refreshToken },
            {
              headers: {
                Authorization: `Bearer ${azureToken}`,
                Accept: "application/json",
                "Content-Type": "application/json",
              },
            }
          );

          const { accessToken, refreshToken: newRefreshToken } = res.data;

          localStorage.setItem(KEY_ACCESS_TOKEN, accessToken.token);
          localStorage.setItem(KEY_REFRESH_TOKEN, newRefreshToken.token);

          // Update the original request with the new access token
          error.config.headers["Authorization"] = `Bearer ${accessToken.token}`;
          error.config.headers["access-token"] = accessToken.token;

          // Retry the original request with the new token
          return axios(error.config);
        } catch (err) {
          console.error("Token refresh failed:", err);
          localStorage.clear();
          window.location.reload();
          return Promise.reject(err);
        }
      }
    }
  );
};

export const getEventsData = async () => {
  try {
    const path = "v1/event/eventmanagement";
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getRegisterEvents = async (contactId: any) => {
  try {
    const path = `v1/event/getRegisterEvent?contactId=${contactId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const ContactDeactivationSentOtp = async (contactId: string) => {
  try {
    const path = `/v1/auth/contactdeactivation/sendotp?contactId=${contactId}`;
    const res = await axiosInstance.post(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const ContactDeactivate = async (payload: any) => {
  try {
    const path = `/v1/auth/contact/deactivate`;
    const res = await axiosInstance.post(path, payload); // Pass payload here
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const ContactDeactivationVerifyOtp = async (contactId: string, otp: string) => {
  try {
    const path = `/v1/auth/contactdeactivation/verify?contactId=${contactId}&otp=${otp}`;
    const res = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getOtp = async (email: string, reset: boolean) => {
  try {
    const path = `/v1/auth/send/otp?email=${email}&update=${reset}`;
    const res = await axiosInstance.post(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const verifyOtp = async (data: VerifySignUpFormData) => {
  try {
    const path = `/v1/auth/verify/otp`;
    const res = await axiosInstance.post(path, data);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const verifyOtpReset = async (email: string, otp: string) => {
  try {
    const path = `/v1/auth/verify/otpwithContact?email=${email}&otp=${otp}`;
    const res = await axiosInstance.post(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const loginSSO = async (email: string) => {
  try {
    const path = `/v1/auth/sso/user/verify?email=${email}`;
    const response = axiosInstance.get(path);
    return response;
  } catch (error) {
    return error;
  }
};

export const authLogin = async (data: LoginData) => {
  try {
    const path = `/v1/auth/login/verify`;
    const res: any = await axiosInstance.post(path, data);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const resetPassword = async (data: LoginData) => {
  try {
    const path = `/v1/auth/login/reset`;
    const res: any = await axiosInstance.post(path, data);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const socialLinks = async (data: LoginData) => {
  try {
    const path = `/v1/auth/linkedinintegration`;
    const res: any = await axiosInstance.post(path, data);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getAffiliations = async (contactId: string) => {
  try {
    const path = `/v1/courses/get/affiliation?accountId=${contactId}`;
    const res: any = await axiosInstance.get(path);
    if (res && res.data) {
      return res.data;
    }
    return res;
  } catch (error: any) {
    throw error;
  }
};

export const getCourses = async (contactId: string, courseId: string) => {
  try {
    const path = `/v1/courses/get/courses?accountId=${contactId}&courseId=${courseId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getLMSRedirectionUrl = async (courseId: string) => {
  try {
    const path = `/v1/brightspace/sso`;
    const res: any = await axiosInstance.post(path, {
      courseId,
    });
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getEvents = async (contactId: string) => {
  try {
    const path = `/v1/event/fetch/user/events?accountId=${contactId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getExamConnectionsByStudent = async (studentId: string) => {
  try {
    const encodedId = encodeURIComponent(studentId);
    const endpoint = encodeURIComponent(`/services/apexrest/studentexamportal/retrieve/examConnectionsByStudent?studentId=${encodedId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getHallTickets = async (contactId: string, examCycleId: string) => {
  try {
    const encodedContactId = encodeURIComponent(contactId);
    const encodedExamCycleId = encodeURIComponent(examCycleId);
    const endpoint = encodeURIComponent(`/services/apexrest/studentexamportal/retrieve/hallTickets?contactId=${encodedContactId}&examCycleId=${encodedExamCycleId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getHallTicketPDF = async (contactId: string, examCycleId: string) => {
  try {
    const encodedContactId = encodeURIComponent(contactId);
    const encodedExamCycleId = encodeURIComponent(examCycleId);
    const endpoint = encodeURIComponent(`/services/apexrest/hallticket/v4/pdf?examCycleId=${encodedExamCycleId}&contactId=${encodedContactId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf',
      },
    });
    
    let pdfData = res && res.data ? res.data : res;
    
    if (pdfData instanceof Blob) {
      if (pdfData.type !== 'application/pdf' && pdfData.type !== '') {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            resolve(new Blob([arrayBuffer], { type: 'application/pdf' }));
          };
          reader.onerror = reject;
          reader.readAsArrayBuffer(pdfData);
        });
      }
      if (pdfData.type === '') {
        return new Blob([pdfData], { type: 'application/pdf' });
      }
    }
    
    return pdfData;
  } catch (error) {
    return error;
  }
};

export const getResultPublish = async (studentId: string) => {
  try {
    const encodedStudentId = encodeURIComponent(studentId);
    const endpoint = encodeURIComponent(`/services/apexrest/studentexamportal/retrieve/resultPublish?studentId=${encodedStudentId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getGradeCards = async (studentId: string) => {
  try {
    const encodedStudentId = encodeURIComponent(studentId);
    const endpoint = encodeURIComponent(`/services/apexrest/studentexamportal/retrieve/gradeCards?studentId=${encodedStudentId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getRequestExams = async (contactId: string) => {
  try {
    const encodedContactId = encodeURIComponent(contactId);
    const endpoint = encodeURIComponent(`/services/apexrest/studentexamportal/retrieve/requestExams?contactId=${encodedContactId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getImageAsBase64 = async (imageUrl: string) => {
  try {
    const encodedUrl = encodeURIComponent(imageUrl);
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/image-proxy?url=${encodedUrl}`);
    if (res && res.data && res.data.success && res.data.data?.base64) {
      return res.data.data.base64;
    }
    throw new Error("Failed to convert image to base64");
  } catch (error: any) {
    console.error("Error converting image to base64:", error);
    throw error;
  }
};

export const getExamSlotSetup = async (examId: string) => {
  try {
    const encodedExamId = encodeURIComponent(examId);
    const endpoint = encodeURIComponent(`/services/apexrest/studentexamportal/retrieve/examSlotSetup?examId=${encodedExamId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const generateExamScheduleLink = async (payload: {
  student_id: string;
  course_id: string;
  slot_details: { slot_start_time: string; slot_end_time: string };
  email_id: string;
  request_type?: number;
}) => {
  try {
    const path = `/schedule/generate-link`;

    const res: any = await axiosInstance.post(path, {
      ...payload,
      request_type: payload.request_type ?? 1,
    });

    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const createSlotScheduled = async (payload: {
  Exam_Connection__c: string;
  Course_ID__c: string;
  Student_ID__c: string;
  Exam__c: string;
  Schedule_URL__c?: string;
  Slot_scheduled__c?: string;
  Exam_Slot_Setup__c?: string;
  Start_Date_Time__c: string;
  End_Date_Time__c: string;
}) => {
  try {
    const path = `/exams/slot-schedule`;

    const res: any = await axiosInstance.post(path, payload);

    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const createSlotScheduledRecords = async (payloads: Array<{
  Exam__c: string;
  Student_ID__c: string;
  Exam_Connection__c: string;
  Start_Date_Time__c: string;
  End_Date_Time__c: string;
  Exam_Slot_Setup__c?: string;
  Course_ID__c?: string;
}>) => {
  try {
    if (!payloads || payloads.length === 0) {
      return {
        success: false,
        error: "No payloads provided",
        results: [],
      };
    }

    const path = `/salesforce?endpoint=services/data/v60.0/composite/sobjects`;
    
    const compositePayload = {
      allOrNone: false,
      records: payloads.map((payload) => ({
        attributes: {
          type: "Slot_scheduled__c",
        },
        ...payload,
      })),
    };

    const res: any = await axiosInstance.post(path, compositePayload);
    
    const results = (res?.data?.results || []).map((result: any, index: number) => {
      if (result.success) {
        return {
          success: true,
          data: result,
          payload: payloads[index],
        };
      } else {
        const errorMessage = result.errors?.[0]?.message || 
                           result.errors?.[0]?.statusCode || 
                           result.errors?.[0]?.fields?.join(", ") ||
                           "Failed to create slot scheduled record";
        return {
          success: false,
          error: errorMessage,
          payload: payloads[index],
        };
      }
    });

    if (results.length === 0 && res?.data) {
      return {
        success: true,
        results: payloads.map((payload, index) => ({
          success: true,
          data: res.data,
          payload,
        })),
      };
    }

    return {
      success: results.every((r: any) => r.success),
      results,
    };
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.errorMessage ||
                        error?.response?.data?.error ||
                        (typeof error?.response?.data === 'string' ? error?.response?.data : null) ||
                        error?.message || 
                        "Failed to create slot scheduled records";
    
    return {
      success: false,
      error: errorMessage,
      results: payloads.map((payload) => ({
        success: false,
        error: errorMessage,
        payload,
      })),
    };
  }
};



export const getCategoryPicklistValue = async () => {
  try {
    const path = `/v1/event/CategoryPicklistValue`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getTypeofStudyPicklistValue = async () => {
  try {
    const path = `/v1/event/typeofStudyPicklistValue`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getTypeofGradePicklistValue = async () => {
  try {
    const path = `/v1/event/typeofGradePicklistValue`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getTargetAudiencePicklistValue = async () => {
  try {
    const path = `/v1/event/TargetAudiencePicklistValue`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getEmployeedetailsPicklistValue = async () => {
  try {
    const path = `/v1/event/getEmployeedetailsPicklistValues`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getEmployeedetailsLocationPicklistValue = async () => {
  try {
    const path = `/v1/event/getEmployeedetailsLocationPicklistValues`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getEvent = async (id: string) => {
  try {
    const path = `/v1/event/fetch/event/ById?eventId=${id}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getFileUrl = async (data: File) => {
  try {
    const path = `/v1/event/create/publicUrl`;
    const form = new FormData();
    form.append("file", data);
    const res: any = await axiosInstance.post(path, form);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const hostEvent = async (payload: EventFormValues) => {
  try {
    const path = `/v1/event/create/event/schedule`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const registerEvent = async (payload: RegisterEvent) => {
  try {
    const path = `/v1/event/new/registration`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const eventPayment = async (payload: PaymentData) => {
  try {
    const path = `/v1/payment/generate/payment`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const cancelEvent = async (id: string) => {
  try {
    const path = `/v1/event/cancel/registration?eventRegistrationId=${id}`;
    const res: any = await axiosInstance.delete(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const deleteEmployment = async (id: string) => {
  try {
    const path = `/v1/user/deleteemployment/${id}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const deleteAcademicRecord = async (id: string) => {
  try {
    const path = `/v1/user/deleteacedamicrecord/${id}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const deleteCertificate = async (id: string) => {
  try {
    const path = `/v1/user/deletecertificate/${id}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getOptions = async (objectName: string, fieldName: string) => {
  try {
    const path = `/v1/helper/getDropDownValues?objectName=${objectName}&fieldName=${fieldName}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getAllJobs = async (conatctId: string) => {
  try {
    const path = `/v1/job/registred/job?contactId=${conatctId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getJob = async (jobId: string) => {
  try {
    const path = `/v1/job/individual/job?jobId=${jobId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const jobApply = async (payload: ApplyJob) => {
  try {
    const path = `/v1/job/apply/job`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postJob = async (payload: FormPostJob) => {
  try {
    const path = `/v1/job/post/job`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getFaq = async () => {
  try {
    const path = `/v1/support/get/faq`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getTicketHistory = async (accountId: string) => {
  try {
    const path = `/v1/support/get/user/case?userId=${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getCourseCalendar = async (accountId: string, startDate: string, endDate: string) => {
  try {
    const path = `/v1/brightspace/coursecalandar?userId=${accountId}&startDate=${startDate}&endDate=${endDate}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getCaseDetails = async (caseId: string) => {
  try {
    const path = `/v1/support/getCaseDetails?caseId=${caseId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getNetworks = async (accountId: string) => {
  try {
    const path = `/v1/networks/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getRecentAlumniDetail = async () => {
  try {
    const path = `/v1/networks/home/getRecentAlumniDetail`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const cancelService = async (id: string) => {
  try {
    const path = `/v1/support/delete/case?caseId=${id}`;
    const res: any = await axiosInstance.delete(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const replay = async (id: string) => {
  try {
    const path = `/v1/support/replay?caseid=${id}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getServices = async () => {
  try {
    const path = `/v1/support/get/service`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getServicesById = async (accountId: string) => {
  try {
    const path = `/v1/support/get/service/Id?serviceid=${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
export const getEventServicesById = async (eventId: string) => {
  try {
    const path = `/v1/support/get/service/Id?eventid=${eventId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getServicesOffering = async () => {
  try {
    const path = `/v1/support/get/serviceoffering`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postQuestionnaire = async (payload: Questionnaire) => {
  try {
    const path = `/v1/support/questionnaire`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
export const postEventQuestionnaire = async (payload: Questionnaire) => {
  try {
    const path = `/v1/event/submitFeedback`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postJobFavorite = async (payload: FormPostJobFavorite) => {
  try {
    const path = `/v1/favourite/add/favorite`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const raiseComplaint = async (payload: FormPostJobFavorite) => {
  try {
    const path = `/v1/support/post/case`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
export const getJobFavorite = async (accountId: string) => {
  try {
    const path = `/v1/favourite/get/user/favorite?accountId=${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postBasicProfileSetting = async (payload: any) => {
  try {
    const path = `/v1/user/profile/update`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getFundList = async (id: string) => {
  try {
    const path = `/v1/fund/list`;
    const res: any = await axiosInstance.get(`${path}/${id}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const donation = async (payload: any) => {
  try {
    const path = `/v1/fund/donation/create`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const campaign = async (payload: any) => {
  try {
    const path = `/v1/fund/campaign/create`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postCampaign = async (payload: any, userId: string) => {
  try {
    const path = `/v1/fund/update/${userId}`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const createIntrest = async (payload: any) => {
  try {
    const path = `/v1/fund/createintrest`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getFundEnrollments = async (fundId: string) => {
  try {
    const path = `/v1/fund/fundEnrollments/${fundId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const survey = async (payload: any) => {
  try {
    const path = `/v1/survey/create`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
export const getBasicProfileSetting = async (accountId: string) => {
  try {
    const path = `/v1/user/profile/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postPreferenceSettings = async (payload: PreferenceDetails) => {
  try {
    const path = `/v1/settings/preference/update`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
export const postPreferenceSetting = async (payload: PreferenceDetails) => {
  try {
    const path = `/v1/settings/preferences/updates`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getPreferenceSetting = async (accountId: string) => {
  try {
    const path = `/v1/settings/preference/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postEventPreferenceSetting = async (payload: EventPreferenceDetails) => {
  try {
    const path = `/v1/settings/eventpreference/update`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getEventPreferenceSetting = async (accountId: string) => {
  try {
    const path = `/v1/settings/eventpreference/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postMentorShipPreferenceSetting = async (payload: MentorshipPreferenceDetails) => {
  try {
    const path = `/v1/settings/mentorshippreference/update`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getMentorShipPreferenceSetting = async (accountId: string) => {
  try {
    const path = `/v1/settings/mentorshippreference/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postJobPreferenceSetting = async (payload: JobPreferenceDetails) => {
  try {
    const path = `/v1/settings/jobpreference/update`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getJobPreferenceSetting = async (accountId: string) => {
  try {
    const path = `/v1/settings/jobpreference/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getJobs = async (accountId: string) => {
  try {
    const path = `/v1/job/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getJobsDetailsById = async (jobId: string) => {
  try {
    const path = `/v1/job/details/${jobId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postJobs = async (payload: JobDetails, accountId: string) => {
  try {
    const path = `/v1/job/create/${accountId}`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getJobsBookmark = async (accountId: string) => {
  try {
    const path = `/v1/job/bookmarks/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postJobsBookmark = async (payload: JobBookmarkDetails, accountId: string) => {
  try {
    const path = `/v1/job/bookmarks/${accountId}`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getJobsApply = async (accountId: string) => {
  try {
    const path = `/v1/job/apply/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postJobsApply = async (payload: JobApplyDetails, accountId: string) => {
  try {
    const path = `v1/job/apply/${accountId}`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getMentorShipTask = async (accountId: string) => {
  try {
    const path = `/v1/mentorship/task/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postMentorShipTask = async (payload: TaskAddDetails) => {
  try {
    const path = `/v1/mentorship/task/create`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getJobsResume = async (accountId: string) => {
  try {
    const path = `/v1/job/resume/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getFeeds = async () => {
  try {
    const path = `/v1/social/feeds`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postJobUploadResume = async (payload: JobUploadDetails) => {
  try {
    const path = `/v1/job/resume`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postEmployeementDetails = async (payload: EmployeementDetails) => {
  try {
    const path = `/v1/user/profile/employmentdetails`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getEmployeementDetails = async (accountId: string | undefined) => {
  try {
    const path = `/v1/user/profile/employmentdetails/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postProfileCertificateDetails = async (payload: CertificateDetails) => {
  try {
    const path = `/v1/user/certificatedetails`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getProfileCertificateDetails = async (accountId: string) => {
  try {
    const path = `/v1/user/certificatedetails/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postAcademicRecords = async (payload: AcademicRecords) => {
  try {
    const path = `/v1/user/academicrecords`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getAcademicRecords = async (accountId: string | undefined) => {
  try {
    const path = `/v1/user/academicrecords/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const postSurvey = async (payload: CreateSurvey) => {
  try {
    const path = `/v1/survey/response`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getSurvey = async (accountId: string | undefined) => {
  try {
    const path = `/v1/survey/${accountId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const eventsCreation = async (payload: any) => {
  try {
    const path = `/v1/event/eventmanagement`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

// export const getEventsData = async () => {
//   try {
//     const path = 'v1/event/eventmanagement';
//     const res: any = await axiosInstance.get(path);
//     return res && res.data ? res.data : res;
//   } catch (error) {
//     return error;
//   }
// };

export const createSession = async (payload: any) => {
  try {
    const path = "/v1/event/session";
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getSessionDetails = async (eventId: string) => {
  try {
    const path = `/v1/event/sessions?eventId=${eventId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const updateSessionFee = async (payload: any) => {
  try {
    const path = `/v1/event/session`;
    const res: any = await axiosInstance.patch(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const cancelcase = async (payload: any) => {
  try {
    const path = `/v1/support/cancelcase/${payload}`;
    const res: any = await axiosInstance.patch(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const reopenCase = async (payload: { caseid: string; reason: string; Status?: string }) => {
  try {
    const path = `/v1/support/reopen`;
    const res: any = await axiosInstance.patch(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};


export const deleteSession = async (payload: any) => {
  try {
    const path = `/v1/event/sessions`;
    const res: any = await axiosInstance.delete(path, { data: payload });
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const eventRegistration = async (payload: any) => {
  try {
    const path = `/v1/event/bookingregistration`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getallGroup = async (contactid: string) => {
  try {
    const path = `/v1/groups/getallGroup?contactid=${contactid}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
export const getGroupEnrollmentDetail = async (contactId: string, groupId: string) => {
  try {
    const path = `/v1/groups/getGroupEnrollmentDetail?contactId=${contactId}&groupId=${groupId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const LeftGroups = async (id: any) => {
  try {
    const path = `/v1/groups/leftGroups?id=${id}`;
    const res: any = await axiosInstance.post(path, id);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const createGroups = async (payload: any) => {
  try {
    const path = `/v1/groups/createGroup`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const groupEnrollment = async (payload: any) => {
  try {
    const path = `/v1/groups/groupEnrollment`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getEventRegistration = async (eventId: string) => {
  try {
    const path = `/v1/event/bookingregistration/${eventId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const surveyResponse = async (surveyId: string) => {
  try {
    const path = `/v1/survey/get/surveyResponse?surveyId=${surveyId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const updateMentorShipTask = async (payload: TaskAddDetails, Id: any) => {
  try {
    const path = `/v1/mentorship/task/update/${Id}`;
    const res: any = await axiosInstance.patch(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
export const deleteMentorShipTask = async (Id: any) => {
  try {
    const path = `/v1/mentorship/task/delete/${Id}`;
    const res: any = await axiosInstance.delete(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const cancelEventRegistration = async (payload: any) => {
  try {
    const path = `/v1/event/bookingregistration`;
    const res: any = await axiosInstance.patch(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const createContact = async (payload: any) => {
  try {
    const path = `v1/user/profile/contact`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const updateaddress = async (payload: any) => {
  try {
    const path = `v1/user/profile/updateaddress`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const createAddress = async (payload: any) => {
  try {
    const path = `v1/user/profile/createAddress`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const updatePreferencesandSkills = async (payload: any) => {
  try {
    const path = `v1/user/profile/updatePreferencesandSkills`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const createPreferencesandSkills = async (payload: any) => {
  try {
    const path = `v1/user/profile/createPreferencesandSkills`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const Logout = async (payload: any) => {
  try {
    const path = `v1/auth/logout`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

//Turbosend Chat APIs
const TurbosendInstance = axios.create({
  baseURL: turbosendURL,
  headers: {
    "Content-Type": "application/json",
  },
});

TurbosendInstance.interceptors.request.use((req) => {
  const userData = JSON.parse(`${localStorage.getItem("user")}`) as AuthUser;
  req.headers["Authorization"] = `Bearer ${userData.turbosendToken.idToken}`;
  return req;
});

TurbosendInstance.interceptors.response.use(
  async (response) => {
    return response;
  },
  async function (error) {
    try {
      const originalRequest = error.config;
      const userData = JSON.parse(`${localStorage.getItem("user")}`) as AuthUser;
      if ((error?.response && error?.response?.status === 401) || error?.response?.status === 500) {
        try {
          const res = await axiosInstance.post("/v1/turbosend/verify_firebase_refreshtoken", {
            refresh_token: userData?.turbosendToken?.refreshToken,
          });
          const newUserData = {
            ...userData,
            turbosendToken: {
              idToken: res.data.result?.id_token,
              refreshToken: res.data.result?.refresh_token,
            },
          };
          localStorage.setItem("user", JSON.stringify(newUserData));
          return axios(originalRequest);
        } catch (err: any) {
          if (err?.response?.data?.success === false && err?.response?.data?.error_message === "jwt expired") {
            window.location.href = "/login";
          } else {
            console.log(err);
          }
        }
      }
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
    return Promise.reject(error);
  }
);

export const registerUser = async (payload: any) => {
  try {
    const path = `/api/users/create`;
    const res = await TurbosendInstance.post(path, payload);
    return res;
  } catch (err) {
    console.log(err);
  }
};

export const createChatRoom = async (payload: any) => {
  try {
    const path = `/api/chatrooms/create`;
    const res = await TurbosendInstance.post(path, payload);
    return res;
  } catch (err) {
    console.log(err);
  }
};

export const getChatRoom = async (Id: any) => {
  try {
    const path = `/api/chatrooms/userId?userId=${Id}`;
    const res = await TurbosendInstance.get(path);
    return res;
  } catch (err) {
    console.log(err);
  }
};

export const getCommonChatRoom = async (Id1: any, Id2: any) => {
  try {
    const path = `/api/chatrooms/commonChatRooms?userId1=${Id1}&userId2=${Id2}`;
    const res = await TurbosendInstance.get(path);
    return res;
  } catch (err) {
    console.log(err);
  }
};

export const GetRecentMessages = async (Id: any, currentPage: any, perPage: any) => {
  try {
    const path = `/api/chatrooms/get/recent-messages/${Id}`;
    const res = await TurbosendInstance.get(path, {
      params: {
        currentPage,
        perPage,
      },
    });
    return res;
  } catch (err) {
    console.log(err);
  }
};

export const GetlastMessage = async (Id: any) => {
  try {
    const path = `/api/chatrooms/chatRoomId?1&1&chatRoomIds=${Id}`;
    const res = await TurbosendInstance.get(path, {});
    return res;
  } catch (err) {
    console.log(err);
  }
};

export const TurboSendFileUpload = async (data: any) => {
  try {
    const path = `/api/uploadFile`;
    const res = await TurbosendInstance.post(path, data);
    return res;
  } catch (err) {
    console.log(err);
  }
};

export const registerDevice = async (data: any) => {
  try {
    const path = `v1/turbosend/registerdevice`;
    const res = await axiosInstance.post(path, {
      userId: data.userId,
      deviceType: "web",
      token: data.token,
    });
    return res && res.data ? res.data : res;
  } catch (err) {
    console.log(err);
  }
};

export const requestForOTP = async (payload: { contact: string; isNoncontactBased: boolean }) => {
  const path = `salesforce?endpoint=${encodeURIComponent(`/services/apexrest/PFSOtp/send?contact=${payload.contact}&isNoncontactBased=${payload.isNoncontactBased}`)}`;
  const res = await axiosInstance.post(`${appConfig?.apiBaseUrl}/${path}`);
  return res && res.data ? res.data : null;
};

export const verifyOTP = async (payload: any) => {
  const path = `verify/pfs/otp?contact=${payload.id}&otpentered=${payload.otp}`;
  const res = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
  var cour = res.data.userCourse;
  return res && res.data ? res.data : null;
};

export const getStudentInfo = async (contactId: string) => {
  const res = await axiosInstance.get(`${appConfig?.apiBaseUrl}/salesforce?endpoint=services/apexrest/ContactDetail/${contactId}`);
  return res && res.data ? res.data : null;
};
// Node code been maintained in supportservice.js, supportcontroller.js, supportroutes.js
export const getStudentAffiliation = async (recordId: string) => {
  try {
    const path = `/v1/support/student-affiliation/${recordId}`;
    const res: any = await axiosInstance.get(path);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};
// CBCS
export const getStudentProgramData = async (contactId: string) => {
  const res = await axiosInstance.get(
    `${appConfig?.apiBaseUrl}/salesforce?endpoint=services/apexrest/studentProgramData&contactId=${contactId}`
  );
  return res && res.data ? res.data : null;
};
export const getCourseEnrollments = async (termId: string, contactId: string) => {
  const endpoint = encodeURIComponent(
    `services/apexrest/courseenrollments?termId=${termId}&contactId=${contactId}`
  );

  const res = await axiosInstance.get(
    `${appConfig?.apiBaseUrl}/salesforce?endpoint=${endpoint}`
  );

  return res && res.data ? res.data : null;
};


export const getProgramEnrollmentById = async (programEnrollmentId: string) => {
  const res = await axiosInstance.get(
    `${appConfig?.apiBaseUrl}/salesforce?endpoint=services/apexrest/programEnrollments/${programEnrollmentId}`
  );
  return res && res.data ? res.data : null;
};


export const createProgramEnrollment = async (payload: {
  hed__Contact__c: string;
  hed__Affiliation__c: string;
  hed__Account__c: string;
  Term__c: string;
  hed__Start_Date__c?: string;
  hed__End_Date__c?: string;
}) => {
  try {
    const path = `/salesforce?endpoint=services/data/v60.0/sobjects/hed__Program_Enrollment__c/`;
    const res = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};



export const createBulkCourseEnrollment = async (payload: {
  ProgramEnrollmentId: string;
  ContactId: string;
  connections: Array<{
    CourseEnrollmentId: string;
    CourseOfferingId: string;
    CourseEnrollmentManagementId: string;
  }>;
}) => {
  try {
    const path = `salesforce?endpoint=${encodeURIComponent(`/services/apexrest/courseconnections`)}`;
    const res = await axiosInstance.post(`${appConfig?.apiBaseUrl}/${path}`, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};


export const getPathwayEnrollments = async (programPlanId: any, accountId: any) => {
  const res = await axiosInstance.get(
    `${appConfig?.apiBaseUrl}/salesforce?endpoint=services/apexrest/getPathwayEnrollments/${programPlanId}/${accountId}`,
  );
  return res?.data ?? null;
};

export const createPathwayConnection = async (payload: {
  Contact__c: string;
  Pathway_Enrollment__c: string;
  Program_Pathway__c: string;
}) => {
  try {
    const path = `/salesforce?endpoint=services/data/v60.0/sobjects/Pathway_Connection__c/`;
    const res = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

// Fee Payment
export const getFeesReceipt = async (contactId: string) => {
  try {
    const encodedContactId = encodeURIComponent(contactId);
    const endpoint = encodeURIComponent(`/services/apexrest/fees/receipt?contactId=${encodedContactId}`);
    const path = `salesforce?endpoint=${endpoint}`;
    const res: any = await axiosInstance.get(`${appConfig?.apiBaseUrl}/${path}`);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getReceiptsByContact = async (contactId: string) => {
  const res = await axiosInstance.get(
    `${appConfig?.apiBaseUrl}/salesforce?endpoint=services/apexrest/getReceiptsByContact/${contactId}`
  );
  return res && res.data ? res.data : null;
};

export const initiateICICIPayment = async (paymentData: any)=>{
  try {
    const res = await axios.post(
      `${appConfig?.apiBaseUrl}/icici/payments/initiate`,
      paymentData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'access-token': localStorage.getItem('login_access_token'),
          'ngrok-skip-browser-warning': 'true' // Skip ngrok browser warning
        },
        timeout: 15000
      }
    );
    return res && res.data ? res.data : null;
  } catch (error) {
    console.error('ICICI Payment initiation error:', error);
    throw error;
  }
};

export const initiateICICIExamRequestPayment = async (paymentData: any)=>{
  try {
    // const baseUrl = 'http://localhost:4000/api'; 
    const baseUrl = `${appConfig?.apiBaseUrl}`; 
    
    const res = await axios.post(
      `${baseUrl}/icici/payments/exam-requests/initiate`,
      paymentData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'access-token': localStorage.getItem('login_access_token'),
          'ngrok-skip-browser-warning': 'true'
        },
        timeout: 15000
      }
    );
    return res && res.data ? res.data : null;
  } catch (error) {
    console.error('ICICI Exam Request Payment initiation error:', error);
    throw error;
  }
};

export const getAnnouncements = async () => {
  try {
    const res = await axiosInstance.get(`${appConfig?.apiBaseUrl}/sf/announcements`);
    return res && res.data ? res.data : null;
  } catch (error) {
    console.error('Get Announcements error:', error);
    return error;
  }
};

export const createOrUpdateCodeTantraStudent = async (payload: {
  uid: string;
  email: string;
  name: string;
  password?: string;
  phoneNumber: string;
  countryCode?: string;
  addGroups?: string[];
  roles?: string[];
  extra?: any;
}) => {
  try {
    const path = `/v1/codetantra/user`;
    const res: any = await axiosInstance.post(path, payload);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};

export const getCodeTantraSSOUrl = async (contactId?: string, rd?: string, rollNumber?: string) => {
  try {
    let finalRollNumber: string | undefined = rollNumber;
    if (!finalRollNumber && contactId) {
      try {
        const affiliationsResponse = await getAffiliations(contactId);
        const affiliations = Array.isArray(affiliationsResponse) 
          ? affiliationsResponse 
          : (affiliationsResponse?.data || affiliationsResponse?.result || []);
        const currentAffiliation = affiliations.find((aff: any) => aff.hed__Status__c === "Current") || affiliations[0];
        
        if (currentAffiliation?.Roll_Number__c) {
          finalRollNumber = currentAffiliation.Roll_Number__c;
        }
      } catch (error) {
        console.error("Error fetching affiliation:", error);
      }
    }

    const path = `/v1/codetantra/sso`;
    const params = new URLSearchParams();

    if (finalRollNumber) params.append("uid", finalRollNumber);
    if (rd) params.append("rd", rd);

    const queryString = params.toString();
    const fullPath = queryString ? `${path}?${queryString}` : path;

    const res: any = await axiosInstance.get(fullPath);
    return res && res.data ? res.data : res;
  } catch (error) {
    return error;
  }
};